import React, { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { getStorage } from "../auth/utils";
import { EDIT_USER, GET_USER } from "../graphql/userGraphql";
import { Modal, ModalActions } from "./Modal";
import ModalTitle from "./Modal/ModalTitle";
import ModalBody from "./Modal/ModalBody";

const EMAIL_PROMPT_DISMISS_MS = 24 * 60 * 60 * 1000;
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SmsLoginEmailPromptModal = ({
  user,
  auth,
  axiosGraphQL,
  errorLog,
  snackBar,
  t,
}) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const dismissalKey = user?._id ? `emailPromptDismissedAt:${user._id}` : null;

  useEffect(() => {
    if (!user?._id || !user?.mobile || user?.email?.trim()) {
      setOpen(false);
      return;
    }

    if (getStorage().getItem("impersonate")) {
      setOpen(false);
      return;
    }

    if (getStorage().getItem("lastLoginMethod") !== "sms") {
      setOpen(false);
      return;
    }

    const dismissedAt = Number(localStorage.getItem(dismissalKey));
    const wasDismissedRecently =
      dismissedAt && Date.now() - dismissedAt < EMAIL_PROMPT_DISMISS_MS;
    setOpen(!wasDismissedRecently);
  }, [dismissalKey, user?._id, user?.mobile, user?.email]);

  const handleDismiss = () => {
    if (dismissalKey) {
      localStorage.setItem(dismissalKey, Date.now().toString());
    }
    setOpen(false);
  };

  const handleSave = () => {
    const normalizedEmail = email.trim().toLowerCase();
    if (!emailRegex.test(normalizedEmail)) {
      setError(t("invalid_email"));
      return;
    }

    setError("");
    setSaving(true);
    axiosGraphQL({ query: EDIT_USER, variables: { email: normalizedEmail } })
      .then(async (response) => {
        if (response.data?.errors?.length) {
          throw new Error(
            response.data.errors[0]?.message || "error_during_login"
          );
        }

        let refreshedUser;
        try {
          const userResponse = await axiosGraphQL({ query: GET_USER });
          refreshedUser = userResponse.data?.data?.me?.user;
          if (!refreshedUser) {
            throw new Error("error_during_login");
          }
          const refreshedClubs = userResponse.data.data.me.clubs;
          const selectedClubId =
            getStorage().getItem("activeClubId") || user?.club?._id;
          const selectedClub = refreshedClubs?.find(
            (club) => club?._id === selectedClubId
          );
          refreshedUser.club =
            selectedClub || user?.club || userResponse.data.data.me.club;
          refreshedUser.clubs = refreshedClubs;
          if (!refreshedUser.email) {
            refreshedUser.email = normalizedEmail;
          }
        } catch (getUserError) {
          errorLog(
            getUserError,
            "AppShell",
            "SmsLoginEmailPromptModal",
            "GET_USER"
          );
          refreshedUser = {
            ...user,
            _id: user?._id,
            mobile: user?.mobile,
            roles: user?.roles,
            club: user?.club,
            clubs: user?.clubs,
            email: normalizedEmail,
          };
        }

        auth.setAuthState({
          user: refreshedUser,
          token: getStorage().getItem("token"),
        });
        getStorage().setItem("user", JSON.stringify(refreshedUser));
        if (dismissalKey) {
          localStorage.removeItem(dismissalKey);
        }
        setOpen(false);
        snackBar.setOptions({
          alertMessage: t("email_saved_successfully"),
          alertSeverity: "success",
        });
        snackBar.setSnackbarOpen(true);
      })
      .catch((error) => {
        errorLog(error, "AppShell", "SmsLoginEmailPromptModal", "EDIT_USER");
        setError(t(error.message) || t("error_during_login"));
      })
      .finally(() => setSaving(false));
  };

  return (
    <Modal open={open} onClose={handleDismiss} type="info">
      <ModalTitle title={t("add_email")} />
      <ModalBody>
        <Typography sx={{ mb: 2 }}>{t("add_email_body")}</Typography>
        <TextField
          autoFocus
          fullWidth
          type="email"
          value={email}
          onChange={({ target }) => setEmail(target.value)}
          onBlur={() => setEmail((value) => value.trim().toLowerCase())}
          error={!!error}
          helperText={error}
          placeholder={t("email_placeholder")}
          size="small"
        />
      </ModalBody>
      <ModalActions>
        <Button
          variant="outlined"
          color="primary"
          fullWidth
          onClick={handleDismiss}
        >
          {t("remind_me_later")}
        </Button>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={handleSave}
          disabled={saving}
        >
          {t("save_email")}
        </Button>
      </ModalActions>
    </Modal>
  );
};

export default SmsLoginEmailPromptModal;
